<template>
  <div class="reserved-page">
    <Header />

    <main class="reserved-page__body">
      <section class="reserved-toolbar">
        <div class="reserved-toolbar__heading">
          <div class="reserved-toolbar__heading-text">
            <h1 class="reserved-toolbar__title">Reservations</h1>
            <p class="reserved-toolbar__subtitle">
              Search and filter bookings by guest, date, or status
            </p>
          </div>
          <button
            type="button"
            class="reserved-btn reserved-btn--accent"
            @click="startNewReservation"
          >
            <i class="icon_plus" aria-hidden="true"></i>
            Reserve a room
          </button>
        </div>

        <div class="reserved-filters">
          <div class="reserved-filters__field">
            <label class="reserved-filters__label" for="reserved-customer">
              <i class="icon_profile" aria-hidden="true"></i>
              Customer
            </label>
            <Multiselect
              id="reserved-customer"
              v-model="customer"
              :options="Guests"
              @search-change="SearchCustomer"
              placeholder="Select customer"
              track-by="name"
              :customLabel="(item) => item.name"
            >
              <template slot="singleLabel" slot-scope="{ option }">
                {{ option.name }}
              </template>
            </Multiselect>
          </div>

          <div class="reserved-filters__field">
            <label class="reserved-filters__label" for="reserved-from">
              <i class="icon_calendar" aria-hidden="true"></i>
              From date
            </label>
            <DatePicker
              id="reserved-from"
              v-model="Filter.start"
              compact
              @input="setEndDate"
              placeholder="From date"
            />
          </div>

          <div class="reserved-filters__field">
            <label class="reserved-filters__label" for="reserved-to">
              <i class="icon_calendar" aria-hidden="true"></i>
              To date
            </label>
            <DatePicker
              id="reserved-to"
              v-model="Filter.end"
              compact
              :minDate="endDatevalidator"
              @input="setEndDatevalid"
              placeholder="To date"
            />
          </div>

          <div class="reserved-filters__field">
            <label class="reserved-filters__label" for="reserved-status">
              <i class="icon_tag_alt" aria-hidden="true"></i>
              Status
            </label>
            <Multiselect
              id="reserved-status"
              v-model="status"
              :options="bookingStatuses"
              :loading="loadingBookingStatuses"
              :disabled="loadingBookingStatuses"
              placeholder="Select status"
              track-by="id"
              label="name"
            >
              <template slot="singleLabel" slot-scope="{ option }">
                {{ option.name }}
              </template>
            </Multiselect>
          </div>

          <div class="reserved-filters__actions">
            <button
              type="button"
              class="reserved-btn reserved-btn--primary"
              @click="addRoomFilter"
            >
              <i class="icon_search" aria-hidden="true"></i>
              Search
            </button>
            <button
              type="button"
              class="reserved-btn reserved-btn--ghost"
              @click="ClearFilter"
            >
              <i class="icon_close_alt2" aria-hidden="true"></i>
              Clear
            </button>
          </div>
        </div>
      </section>

      <section class="reserved-table-card">
        <div class="reserved-table-card__head">
          <span class="reserved-table-card__count">
            <i class="icon_documents_alt" aria-hidden="true"></i>
            {{ totalReservations }} reservation{{ totalReservations === 1 ? '' : 's' }}
          </span>
          <span v-if="listLoading" class="reserved-table-card__loading">Loading…</span>
        </div>

        <div class="reserved-table-wrap">
          <table v-if="roomsData.length > 0" class="reservation-table">
            <thead>
              <tr>
                <th class="reserved-col--left">Date</th>
                <th class="reserved-col--left">Reference</th>
                <th class="reserved-col--left">Guest</th>
                <th class="reserved-col--center">Rooms</th>
                <th class="reserved-col--center reserved-col--status">Status</th>
                <th class="reserved-col--right">Total</th>
                <th class="reserved-col--right">Paid</th>
                <th class="reserved-col--center">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="rooms in roomsData"
                :key="rooms.id"
                class="reservation-table__row"
                @click="NavigateToView(rooms.id)"
              >
                <td class="reserved-col--left" data-label="Date">
                  <span class="reserved-cell-date">
                    <i class="icon_calendar" aria-hidden="true"></i>
                    {{ rooms.date | formatDate }}
                  </span>
                </td>
                <td class="reserved-col--left" data-label="Reference">
                  <span class="reserved-ref">{{ rooms.reference_number }}</span>
                </td>
                <td class="reserved-col--left" data-label="Guest">
                  <span class="reserved-guest">
                    <i class="icon_profile" aria-hidden="true"></i>
                    {{
                      rooms.guest && rooms.guest.name ? rooms.guest.name : '—'
                    }}
                  </span>
                </td>
                <td
                  class="reserved-col--center reserved-col--rooms"
                  data-label="Rooms"
                >
                  <span
                    v-for="item in rooms.rooms"
                    :key="`${rooms.id}-${item}`"
                    class="room-pill"
                  >
                    {{ item }}
                  </span>
                </td>
                <td class="reserved-col--center reserved-col--status" data-label="Status">
                  <span class="reserved-status-cell">
                    <span
                      class="status-tag"
                      :class="statusClass(rooms)"
                      :style="statusStyle(rooms)"
                      :title="rowStatusLabel(rooms)"
                    >
                      {{ rowStatusLabel(rooms) }}
                    </span>
                  </span>
                </td>
                <td
                  class="reserved-col--right reserved-col--amount"
                  data-label="Total"
                >
                  {{ formatAmount(rooms.total_amount) }}
                </td>
                <td
                  class="reserved-col--right reserved-col--amount"
                  data-label="Paid"
                >
                  {{ formatAmount(rooms.paid_amount) }}
                </td>
                <td
                  class="reserved-col--center reserved-col--action"
                  data-label="Actions"
                >
                  <button
                    type="button"
                    class="reserved-action-btn"
                    title="View reservation"
                    aria-label="View reservation"
                    @click.stop="NavigateToView(rooms.id)"
                  >
                    <i class="icon_search-2" aria-hidden="true"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <div v-if="!listLoading && roomsData.length === 0" class="reserved-empty">
            <img
              src="@/assets/img/noresultsGif.gif"
              alt="No results"
              class="no-results"
            />
            <p>No reservations found</p>
            <span>Try adjusting your search filters, or start a new booking.</span>
            <button
              type="button"
              class="reserved-btn reserved-btn--accent reserved-empty__cta"
              @click="startNewReservation"
            >
              <i class="icon_plus" aria-hidden="true"></i>
              Reserve a room
            </button>
          </div>
        </div>

        <div class="reserved-pagination">
          <Pagination
            :url="reservationListUrl"
            :page-size="15"
            @paginationOptions="setPaginationOptions"
            @response="setResponseData"
            @loading="listLoading = $event"
            ref="pagination"
          />
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import Multiselect from "vue-multiselect";

import Courosel from "@/components/base/Caurosel/index";
import { DatePicker } from "@/components/base/index";
import Pagination from '@/components/Share/Pagination/index';
const MyComponent = {
  template: `
        <span class="my-icon">{{ opened ? 'I am open' : ' I am closed' }}</span>
    `,
  props: {
    opened: {
      type: Boolean,
      default: false,
    },
  },
};
import { CustomModal as CModal } from "@/components/base/index.js";
import SingleRoomViewForm from "../components/Share/SingleRoomView/index";
import CheckboxList from "../components/Share/CheckboxList/index";
import VueRangeSlider from "vue-range-component";
import "vue-range-component/dist/vue-range-slider.css";
import axios from "@/plugins/axios";
import { Header } from "@/components/index.js";
import { RefreshButton } from "@/components/index.js";
import { mapState } from "vuex";

import moment from "moment";
export default {
  name: "ReservedRoomList",
  data() {
    return {
      paginationOptions: {},
      totalReservations: 0,
      listLoading: false,
      status: null,
      customer: "",
      bookingStatuses: [],
      loadingBookingStatuses: false,
      selected: {
        key: "1",
        text: "select",
      },
      completeBill: false,
      selectedMethod: {
        key: "1",
        text: "select",
      },
      Filter: {
        start: null,
        end: null,
      },
      iconComponent: MyComponent,
      typesLoaded: false,
      FirstLoad: true,
      DateLimit: [
        {
          type: "weekday",
          available: [0, 1, 2, 3, 4, 5, 6],
        },
        {
          type: "weekend",
          available: [1, 2],
        },
        {
          type: "fromto",
          from: moment().subtract(1, "days").format("YYYY-MM-DD"),
        },
      ],
      EndDateLimit: [
        {
          type: "weekday",
          available: [0, 1, 2, 3, 4, 5, 6],
        },
        {
          type: "weekend",
          available: [1, 2],
        },
        {
          type: "fromto",
          from: moment(new Date()).subtract(1, "days").format("YYYY-MM-DD"),
        },
      ],

      startDatevalidator: moment().subtract(0, "days").format("YYYY-MM-DD"),
      endDatevalidator: moment(new Date())
        .subtract(1, "days")
        .format("YYYY-MM-DD"),
      RoomImagesData: [],
      pagination1: {
        descending: true,
        page: 1,
        rowsPerPage: 2,

        sortBy: "fat",
        totalItems: 0,
        rowsPerPageItems: [1, 2, 4, 8, 16],
      },

      pagination: {
        page: 1,
        itemsPerPage: 2,
      },
      page: 1,
      roomsDataImages: [],
      roomsData: [],
      Guests: [],
      roomsDatasingleView: [],
      roomTypes: [],
      range: 1000,
      rangeBackground: {},
      processStyle: {
        backgroundColor: "#589442",
      },
      tooltipStyle: {
        backgroundColor: "#589442",
        borderColor: "#589442",
      },
    };
  },

  components: {
    VueRangeSlider,
    Header,
    CModal,
    CheckboxList,
    Courosel,
    RefreshButton,
    SingleRoomViewForm,
    DatePicker,
    Pagination,
    Multiselect,
  },
  computed: {
    ...mapState("orders", ["FilterDate"]),
    reservationListUrl() {
      const params = [];
      if (this.Filter.start && this.Filter.end) {
        params.push(`start=${this.Filter.start}`);
        params.push(`end=${this.Filter.end}`);
      }
      if (this.customer && this.customer.id) {
        params.push(`guest=${this.customer.id}`);
      }
      if (this.status && this.status.id) {
        params.push(`booking_status_id=${this.status.id}`);
      }
      const query = params.length ? `${params.join("&")}&` : "";
      return `hms/web/reservations?${query}`;
    },
  },

  methods: {
    parseBookingStatusList(payload) {
      if (Array.isArray(payload)) {
        return payload;
      }
      if (payload && Array.isArray(payload.data)) {
        return payload.data;
      }
      return [];
    },
    loadBookingStatuses() {
      this.loadingBookingStatuses = true;
      const applyStatuses = (res) => {
        this.bookingStatuses = this.parseBookingStatusList(res.data);
      };

      return axios
        .get("hms/web/booking-statuses")
        .then(applyStatuses)
        .catch(() =>
          axios
            .get("booking-statuses/search?status=active")
            .then(applyStatuses)
            .catch(() => {
              this.bookingStatuses = [];
            }),
        )
        .finally(() => {
          this.loadingBookingStatuses = false;
        });
    },
    rowStatusLabel(row) {
      if (row.booking_status && row.booking_status.name) {
        return row.booking_status.name;
      }
      return this.formatPaymentStatus(row.status);
    },
    formatPaymentStatus(status) {
      if (!status) return "—";
      if (status === "PartiallyPaid") return "Partially Paid";
      return status;
    },
    statusStyle(row) {
      if (row.booking_status && row.booking_status.color_code) {
        const color = row.booking_status.color_code;
        return {
          backgroundColor: `${color}22`,
          color,
          border: `1px solid ${color}55`,
        };
      }
      return {};
    },
    statusClass(row) {
      if (row.booking_status && row.booking_status.color_code) {
        return {};
      }
      const status = row.status;
      return {
        "status-reserved": status === "Reserved",
        "status-paid": status === "Paid",
        "status-partial": status === "PartiallyPaid",
        "status-cancelled": status === "Cancelled",
      };
    },
    formatAmount(value) {
      const num = Number(value);
      if (Number.isNaN(num)) return "0.00";
      return num.toFixed(2);
    },
    ClearFilter() {
      this.Filter.start = null;
      this.Filter.end = null;
      this.customer = "";
      this.status = null;
      this.addRoomFilter();
    },
    setPaginationOptions(data) {
      this.paginationOptions = data || {};
      const total = Number(data && data.totalItems);
      if (Number.isFinite(total)) {
        this.totalReservations = total;
      }
    },
    SearchReservedRooms() {
      let url = `hms/web/reservations?start=${this.FilterDate.start}&end=${this.FilterDate.end}`;

      url = url + "page=" + this.page + "&&";
      url = url + "size=" + this.pageSize;
      axios
        .get(url)
        .then((res) => {
          this.roomsData = res.data.data;
          this.totalResrvations = res.data.meta.totalItems;
        })
        .catch(() => {});
    },
    setEndDatevalid(startDate) {
      startDate = moment(new Date(startDate));
      this.Filter.end = moment(new Date(startDate)).format("YYYY-MM-DD");
    },
    setEndDate(startDate) {
      startDate = moment(new Date(startDate));
      this.Filter.start = moment(new Date(startDate)).format("YYYY-MM-DD");

      let Enddate = startDate.add(1, "days");
      Enddate = startDate.format("YYYY-MM-DD");
      this.endDatevalidator = startDate.subtract(1, "days");
      this.Filter.end = Enddate;
    },

    NavigateToView(index) {
      this.$router.push({
        path: "/reservation-details/" + index,
      });
    },

    setResponseData(data) {
      this.roomsData = Array.isArray(data) ? data : [];
      this.listLoading = false;
    },
    reloadReservations(page = 1) {
      const pagination = this.$refs.pagination;
      if (!pagination) return;
      pagination.goToPage(page, true);
    },
    startNewReservation() {
      this.$store.commit("orders/updateOrder", []);
      this.$router.push({
        path: "/rooms-booking",
        query: { addRooms: "1" },
      });
    },
    addRoomFilter() {
      this.$store.commit("orders/AddFilterDates", this.Filter);
      this.$nextTick(() => this.reloadReservations(1));
    },
    RefreshData() {
      this.GetRooms();
    },

    GetCustomers() {
      axios
        .get(`/hms/web/guests`)
        .then((res) => {
          this.Guests = res.data.data;

          this.typesLoaded = true;
        })
        .catch(() => {});
    },
    SearchCustomer(event) {
      axios
        .get(`/hms/web/guests/search?query=${event ? event : ""}`)
        .then((res) => {
          this.Guests = res.data.data;

          this.typesLoaded = true;
        })
        .catch(() => {});
    },
    FilterRooms() {
      axios
        .get(
          `hms/web/reservations?start=${this.FilterDate.start}&end=${this.FilterDate.end}`
        )
        .then((res) => {
          this.roomsData = res.data.data;

          this.typesLoaded = true;
        })
        .catch(() => {});
    },
  },
  created() {
    this.listLoading = true;
    this.GetCustomers();
    this.loadBookingStatuses();
  },
};
</script>

<style lang="scss">
.reserved-page .multiselect {
  min-height: 32px;
  font-size: 12px;
}

.reserved-page .multiselect__tags {
  min-height: 32px;
  padding: 4px 28px 0 8px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
}

.reserved-page .multiselect__single,
.reserved-page .multiselect__placeholder,
.reserved-page .multiselect__input {
  font-size: 12px;
  line-height: 22px;
  margin-bottom: 0;
  padding-top: 0;
}

.reserved-page .multiselect__select {
  height: 32px;
  width: 28px;
  padding: 2px 6px;
}

.reserved-page .kot-date-picker,
.reserved-page .kot-date-picker .vd-wrapper,
.reserved-page .kot-date-picker .vd-activator {
  width: 100%;
  min-width: 0;
}

.reserved-page .kot-date-picker .vd-picker__input {
  display: flex;
  align-items: center;
  width: 100%;
  height: 32px !important;
  min-height: 32px !important;
  padding: 0 8px 0 6px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
  box-sizing: border-box;
}

.reserved-page .kot-date-picker .vd-picker__input-icon {
  flex: 0 0 18px;
  margin: 0;
  padding: 0;
}

.reserved-page .kot-date-picker.kot-date-picker--compact .vd-picker__input input,
.reserved-page .kot-date-picker .vd-picker__input input,
.reserved-page .DatePicker input,
.reserved-page .form-control {
  flex: 1 1 auto;
  width: 1% !important;
  min-width: 0 !important;
  height: 30px !important;
  min-height: 0 !important;
  margin: 0;
  padding: 0 4px !important;
  border: 0 !important;
  border-radius: 0;
  background: transparent !important;
  box-shadow: none !important;
  font-size: 12px !important;
  line-height: 30px !important;
  text-align: left !important;
  box-sizing: border-box;
}
</style>

<style scoped>
.reserved-page {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  background: #f5f3f9;
  overflow-x: hidden;
}

.reserved-page__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: none;
  margin: 0;
  padding: calc(60px + env(safe-area-inset-top, 0px)) clamp(12px, 2vw, 24px)
    calc(12px + env(safe-area-inset-bottom, 0px));
  box-sizing: border-box;
  min-height: 0;
}

.reserved-toolbar {
  flex-shrink: 0;
  margin-bottom: clamp(8px, 1.5vw, 12px);
  text-align: left;
}

.reserved-toolbar__heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.reserved-toolbar__heading-text {
  min-width: 0;
  text-align: left;
}

.reserved-toolbar__title {
  margin: 0 0 2px;
  font-size: clamp(1.1rem, 2.5vw, 1.35rem);
  font-weight: 700;
  color: #1e293b;
  letter-spacing: -0.02em;
  line-height: 1.2;
}

.reserved-toolbar__subtitle {
  margin: 0;
  font-size: clamp(0.78rem, 1.8vw, 0.85rem);
  color: #64748b;
  line-height: 1.3;
}

.reserved-filters {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr)) auto;
  gap: clamp(8px, 1.5vw, 12px);
  align-items: end;
  padding: clamp(10px, 2vw, 16px);
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e8edf5;
  box-shadow: 0 4px 18px var(--brand-shadow-sm);
}

.reserved-filters__field {
  min-width: 0;
}

.reserved-filters__label {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  gap: 6px;
  margin-bottom: 6px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
  text-align: left;
}

.reserved-filters__label i {
  font-size: 13px;
  color: var(--brand-primary);
}

.reserved-filters__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
}

.reserved-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border: none;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.1s ease;
  white-space: nowrap;
}

.reserved-btn i {
  font-size: 12px;
  line-height: 1;
}

.reserved-btn--primary {
  background: var(--brand-primary-muted);
  color: var(--brand-primary);
  border: 1px solid var(--brand-primary-border);
  box-shadow: none;
}

.reserved-btn--primary:hover {
  background: var(--brand-primary-fade-12);
  color: var(--brand-primary-hover);
  border-color: var(--brand-primary-border);
}

.reserved-btn--primary i {
  color: var(--brand-primary);
}

.reserved-btn--ghost {
  background: #f8fafc;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.reserved-btn--ghost:hover {
  background: #f1f5f9;
  color: #1e293b;
}

.reserved-btn--accent {
  background: var(--brand-primary);
  color: #fff;
  border: 1px solid var(--brand-primary);
  box-shadow: 0 2px 8px var(--brand-shadow-sm);
}

.reserved-btn--accent:hover {
  background: var(--brand-primary-hover, var(--brand-primary));
  border-color: var(--brand-primary-hover, var(--brand-primary));
  color: #fff;
}

.reserved-btn--accent i {
  color: #fff;
}

.reserved-table-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e8edf5;
  box-shadow: 0 4px 18px var(--brand-shadow-sm);
  overflow: hidden;
}

.reserved-table-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #eef2f7;
  background: linear-gradient(180deg, #fafbfd 0%, #fff 100%);
}

.reserved-table-card__count {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
}

.reserved-table-card__count i {
  color: var(--brand-primary);
  font-size: 15px;
}

.reserved-table-card__loading {
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
}

.reserved-table-wrap {
  flex: 1;
  overflow: auto;
  min-height: 0;
  -webkit-overflow-scrolling: touch;
}

.reservation-table {
  width: 100%;
  min-width: 960px;
  border-collapse: collapse;
}

.reservation-table thead th {
  padding: 8px 12px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #64748b;
  background: #f8fafc;
  border-bottom: 1px solid #e8edf5;
  white-space: nowrap;
}

.reserved-col--left {
  text-align: left;
}

.reserved-col--center {
  text-align: center;
}

.reserved-col--right {
  text-align: right;
}

.reserved-col--rooms {
  vertical-align: middle;
  text-align: center;
}

.reserved-col--status {
  width: 168px;
  min-width: 168px;
  max-width: 168px;
}

.reserved-status-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.reserved-col--rooms .room-pill {
  margin: 2px;
}

.reservation-table tbody tr {
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.15s ease;
  cursor: pointer;
}

.reservation-table tbody tr:hover {
  background: #f8fafc;
}

.reservation-table tbody tr:last-child {
  border-bottom: none;
}

.reservation-table td {
  padding: 8px 12px;
  font-size: 13px;
  line-height: 1.25;
  color: #334155;
  vertical-align: middle;
}

.reserved-col--amount {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: #1e293b;
}

.reserved-col--action {
  width: 72px;
}

.reserved-cell-date,
.reserved-guest {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.reserved-cell-date i,
.reserved-guest i {
  font-size: 13px;
  color: #94a3b8;
}

.reserved-ref {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  font-weight: 600;
  color: var(--brand-primary);
}

.room-pill {
  display: inline-block;
  margin: 1px 3px 1px 0;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  background: var(--brand-primary-muted);
  color: var(--brand-primary);
  vertical-align: middle;
}

.status-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 152px;
  max-width: 100%;
  min-height: 22px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
  letter-spacing: 0.01em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status-reserved {
  background: #fef3c7;
  color: #b45309;
}

.status-paid {
  background: #dcfce7;
  color: #15803d;
}

.status-partial {
  background: #ffedd5;
  color: #c2410c;
}

.status-cancelled {
  background: #fee2e2;
  color: #b91c1c;
}

.reserved-action-btn {
  width: 30px;
  height: 30px;
  padding: 0;
  border: 1px solid var(--brand-primary-border);
  border-radius: 8px;
  background: var(--brand-primary-muted);
  color: var(--brand-primary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.reserved-action-btn:hover {
  background: var(--brand-primary-fade-12);
  border-color: var(--brand-primary-border);
  color: var(--brand-primary-hover);
}

.reserved-action-btn i {
  font-size: 13px;
  margin: 0;
  color: var(--brand-primary);
}

.reserved-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 24px;
  text-align: center;
}

.reserved-empty p {
  margin: 12px 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: #334155;
}

.reserved-empty span {
  font-size: 13px;
  color: #94a3b8;
}

.reserved-empty__cta {
  margin-top: 16px;
}

.no-results {
  width: 220px;
  height: auto;
  opacity: 0.9;
}

.reserved-pagination {
  flex-shrink: 0;
  padding: clamp(10px, 2vw, 16px) clamp(12px, 2vw, 20px);
  border-top: 1px solid #eef2f7;
  background: #fff;
}

.reserved-pagination >>> .Footer1 {
  margin-top: 0;
  border-top: none;
  padding-top: 0;
  width: 100%;
}

.reserved-pagination >>> .pagination-wrapper {
  width: 100%;
  justify-content: space-between;
}

/* Large desktop */
@media (min-width: 1400px) {
  .reservation-table {
    min-width: 100%;
  }
}

/* Laptop / small desktop */
@media (max-width: 1200px) {
  .reserved-filters {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .reserved-filters__actions {
    grid-column: 1 / -1;
    justify-content: flex-end;
  }

  .reservation-table {
    min-width: 880px;
  }
}

/* Tablet landscape */
@media (max-width: 992px) {
  .reserved-filters {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .reserved-table-card__head {
    padding: 12px 14px;
    flex-wrap: wrap;
    gap: 8px;
  }

  .reservation-table thead th,
  .reservation-table td {
    padding: 7px 10px;
    font-size: 12px;
  }

  .reservation-table {
    min-width: 820px;
  }
}

/* Tablet portrait */
@media (max-width: 768px) {
  .reserved-toolbar__heading {
    flex-direction: column;
    align-items: stretch;
  }

  .reserved-toolbar__heading .reserved-btn--accent {
    width: 100%;
  }

  .reserved-filters {
    grid-template-columns: 1fr 1fr;
  }

  .reserved-filters__actions {
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  .reserved-btn {
    width: 100%;
  }

  .reserved-pagination >>> .pagination-wrapper {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .reserved-pagination >>> .pagination-controls {
    display: flex;
    justify-content: center;
    gap: 8px;
    width: 100%;
  }

  .reserved-pagination >>> .display-section {
    justify-content: center;
    width: 100%;
  }
}

/* Mobile — card layout */
@media (max-width: 640px) {
  .reserved-filters {
    grid-template-columns: 1fr;
  }

  .reserved-filters__actions {
    grid-template-columns: 1fr;
  }

  .reserved-table-card {
    border-radius: 12px;
  }

  .reserved-table-wrap {
    padding: 8px 10px 12px;
    overflow-x: visible;
  }

  .reservation-table {
    min-width: 0;
    display: block;
  }

  .reservation-table thead {
    display: none;
  }

  .reservation-table tbody {
    display: block;
  }

  .reservation-table tbody tr {
    display: block;
    padding: 12px 14px;
    margin-bottom: 10px;
    border: 1px solid #eef2f7;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  }

  .reservation-table tbody tr:hover {
    background: #f8fafc;
  }

  .reservation-table td {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 8px 0;
    border: none;
    text-align: right !important;
    width: 100%;
    box-sizing: border-box;
  }

  .reservation-table td::before {
    content: attr(data-label);
    flex: 0 0 auto;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #94a3b8;
    text-align: left;
  }

  .reservation-table td.reserved-col--rooms,
  .reservation-table td.reserved-col--center {
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .reservation-table td.reserved-col--rooms::before,
  .reservation-table td.reserved-col--center::before {
    width: 100%;
    margin-bottom: 4px;
  }

  .reserved-col--status {
    width: auto;
    min-width: 0;
    max-width: none;
  }

  .reservation-table td .status-tag {
    width: auto;
    min-width: 88px;
    max-width: min(100%, 200px);
  }

  .reserved-cell-date,
  .reserved-guest {
    justify-content: flex-end;
    text-align: right;
    max-width: 65%;
  }

  .reserved-ref {
    text-align: right;
    word-break: break-all;
  }

  .reserved-col--action {
    justify-content: space-between;
  }

  .no-results {
    width: min(220px, 70vw);
  }
}

/* Small phones */
@media (max-width: 380px) {
  .reserved-page__body {
    padding-left: 8px;
    padding-right: 8px;
  }

  .reserved-btn {
    font-size: 12px;
    height: 32px;
  }

  .reserved-pagination >>> .Button {
    min-width: 72px;
    padding: 8px 12px;
    font-size: 12px;
  }
}
</style>
